import { Apple, Play, ExternalLink } from "lucide-react";
import { cn } from "../../utils/cn";
import { useTheme } from "../../context/ThemeContext";
import { useLocale } from "../../context/LocaleContext";

const SPECS = [
  { key: "appStore", icon: Apple, labelKey: "appStore" },
  { key: "googlePlay", icon: Play, labelKey: "googlePlay" },
  { key: "website", icon: ExternalLink, labelKey: "visitSite" },
];

export function ProjectLinks({
  links,
  size = "md",
  variant = "chip",
  className,
  strings,
}) {
  const { theme } = useTheme();
  const { t } = useLocale();
  if (!links) return null;
  const standardItems = SPECS.filter((s) => links[s.key]);
  const demos = Array.isArray(links.demos) ? links.demos : [];
  if (standardItems.length === 0 && demos.length === 0) return null;

  const isIcon = variant === "icon";
  const sizes = {
    sm: isIcon ? "h-8 w-8" : "px-3 py-1.5 text-xs",
    md: isIcon ? "h-9 w-9" : "px-3.5 py-2 text-xs",
    lg: isIcon ? "h-11 w-11" : "px-4 py-2.5 text-sm",
  };

  const baseClass = cn(
    "group inline-flex items-center justify-center gap-2 rounded-lg border font-semibold transition-all duration-300 backdrop-blur-md",
    "bg-white/5 hover:bg-white/10 text-white",
    sizes[size]
  );

  const onEnter = (e) => {
    e.currentTarget.style.borderColor = `${theme.accent}99`;
    e.currentTarget.style.color = theme.accent;
    e.currentTarget.style.boxShadow = `0 0 12px ${theme.accent}55`;
  };
  const onLeave = (e) => {
    e.currentTarget.style.borderColor = `${theme.accent}33`;
    e.currentTarget.style.color = "";
    e.currentTarget.style.boxShadow = "";
  };

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {standardItems.map(({ key, icon: Icon, labelKey }) => {
        const label = strings?.[labelKey] ?? key;
        return (
          <a
            key={key}
            href={links[key]}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={label}
            title={label}
            className={baseClass}
            style={{ borderColor: `${theme.accent}33` }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
          >
            <Icon size={isIcon ? 16 : 14} aria-hidden="true" />
            {!isIcon && <span>{label}</span>}
          </a>
        );
      })}

      {demos.map((demo, i) => {
        const label = t(demo.label) || `Demo ${i + 1}`;
        return (
          <a
            key={`demo-${i}`}
            href={demo.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={label}
            title={label}
            className={cn(
              baseClass,
              isIcon && "w-auto px-3"
            )}
            style={{ borderColor: `${theme.accent}33` }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
          >
            <ExternalLink size={isIcon ? 14 : 14} aria-hidden="true" />
            <span className={cn(isIcon && "text-[11px]")}>{label}</span>
          </a>
        );
      })}
    </div>
  );
}
